import { Loading } from "@/components/portal/Loading";
import { Table } from "@/components/Table";
import { BASE_URL } from "@/dashboardQueries";
import { useDarkMode } from "@/hooks";
import { FlowStats, StepRunDecision } from "@/models/flows";
import axios from "axios";
import { toSentenceCase } from "js-convert-case";
import { ArrowLeftIcon, Eye, EyeIcon } from "lucide-react";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "../../Pagination";
import { Modal } from "../../Modal";
import { set } from "lodash";
import { ApprovalStep } from "./steps/ApprovalStep";
import { Search } from "../../Search";
import { FlowRunDetail } from "./FlowRunDetail";

interface Props {
  flowId: string;
}

const fetchFlowStats = async (flowId: string): Promise<FlowStats> => {
  const response = await axios.get(`${BASE_URL}/flows/${flowId}/stats`);
  return response.data;
};

export const Decisions = ({ flowId }: Props) => {
  // General flow stats query
  const {
    data: flowStats,
    isLoading: isFlowStatsLoading,
    isError: isFlowStatsError,
  } = useQuery(["generalFlowStats", flowId], () => fetchFlowStats(flowId), {
    enabled: !!flowId,
  });
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const isDarkMode = useDarkMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const decisionFilter = searchParams.get("decision") || "";
  const [page, setPage] = useState(1);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [decisionToOverride, setSetDecisionToOverride] =
    useState<StepRunDecision | null>(null);
  const [search, setSearch] = useState<string>("");
  const queryClient = useQueryClient();
  const [flowRunModalOpen, setFlowRunModalOpen] = useState(false);
  const [selectedFlowRunId, setSelectedFlowRunId] = useState<string | null>(
    null
  );

  // Get flow run by the selected flow run Id
  const {
    data: selectedFlowRun,
    isLoading: isSelectedFlowRunLoading,
    isError: isSelectedFlowRunError,
  } = useQuery(
    selectedFlowRunId ? ["flowRun", selectedFlowRunId] : [],
    async () => {
      const res = await axios.get(`${BASE_URL}/flow-runs/${selectedFlowRunId}`);
      return res.data;
    },
    {
      enabled: !!selectedFlowRunId,
    }
  );

  // Fetch selected step details if a step is selected
  const {
    data: selectedStep,
    isLoading: isSelectedStepLoading,
    isError: isSelectedStepError,
  } = useQuery(
    selectedStepId ? ["stepDetail", selectedStepId] : [],
    async () => {
      const res = await axios.get(`${BASE_URL}/steps/${selectedStepId}`);
      return res.data;
    },
    {
      enabled: !!selectedStepId,
    }
  );

  // Fetch step run decisions if a step is selected
  const {
    data: stepRunDecisions,
    isLoading: isStepRunDecisionsLoading,
    isError: isStepRunDecisionsError,
  } = useQuery(
    selectedStepId
      ? ["stepRunDecisions", selectedStepId, decisionFilter, page, search]
      : [],
    async () => {
      let url = `${BASE_URL}/step-run-decisions?step=${selectedStepId}&page=${page}`;
      if (decisionFilter) {
        url += `&decision=${decisionFilter}`;
      }
      if (search) {
        url += `&search=${search}`;
      }
      const res = await axios.get(url);
      return res.data;
    },
    {
      enabled: !!selectedStepId,
    }
  );

  // Fetch the step run for override if overrideStepRunId is set
  const {
    data: overrideStepRun,
    isLoading: isOverrideStepRunLoading,
    isError: isOverrideStepRunError,
  } = useQuery(
    decisionToOverride ? ["stepRun", decisionToOverride?.stepRun.id] : [],
    async () => {
      const res = await axios.get(
        `${BASE_URL}/step-runs/${decisionToOverride?.stepRun.id}`
      );
      return res.data;
    },
    {
      enabled: !!decisionToOverride && isOverrideModalOpen,
    }
  );

  return (
    <div className="overflow-x-scroll border-t border-gray-200 dark:border-white/10 h-full p-4">
      <Modal
        isOpen={flowRunModalOpen}
        setIsOpen={setFlowRunModalOpen}
        className="w-1/2"
      >
        <div className="min-w-3xl w-full">
          {selectedFlowRun ? (
            <>
              <h3 className="dark:text-white text-lg mb-4">
                Flow run for{" "}
                <span className="font-semibold">
                  {selectedFlowRun.displayName}
                </span>
              </h3>
              <FlowRunDetail flowRun={selectedFlowRun} />
            </>
          ) : (
            <Loading />
          )}
        </div>
      </Modal>
      <Modal isOpen={isOverrideModalOpen} setIsOpen={setIsOverrideModalOpen}>
        <div>
          {isOverrideStepRunLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loading colour={isDarkMode ? "white" : "black"} />
            </div>
          ) : overrideStepRun ? (
            <div className="min-w-3xl w-full">
              <h3 className="dark:text-white text-lg">
                Overriding decision for {overrideStepRun.step.name} step for{" "}
                <span className="font-semibold">
                  {decisionToOverride?.stepRun.flowRun.displayName}
                </span>
              </h3>
              <ApprovalStep
                override
                stepRun={overrideStepRun}
                onDecisionSuccess={() => {
                  setIsOverrideModalOpen(false);
                  setSelectedStepId(null);
                  queryClient.invalidateQueries([
                    "stepRunDecisions",
                    selectedStepId,
                    decisionFilter,
                    page,
                    search,
                  ]);
                }}
              />
            </div>
          ) : null}
        </div>
      </Modal>
      {isFlowStatsLoading ? (
        <div className="flex items-center justify-center h-full">
          <Loading colour={isDarkMode ? "white" : "black"} />
        </div>
      ) : (
        <div className="mb-40">
          {selectedStepId ? (
            <div>
              <button
                className="mb-2 text-sm hover:underline flex items-center dark:text-zinc-400"
                onClick={() => setSelectedStepId(null)}
              >
                <ArrowLeftIcon className="inline mr-1 h-4 w-4" />
                Back to Overview
              </button>

              {isStepRunDecisionsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loading colour={isDarkMode ? "white" : "black"} />
                </div>
              ) : isStepRunDecisionsError ? (
                <div className="text-red-500">Error loading decisions.</div>
              ) : (
                <>
                  {selectedStep && (
                    <h1 className="text-2xl mb-4">{selectedStep.name}</h1>
                  )}
                  <div>
                    <div className="flex justify-between items-center border-b pb-4 dark:border-white/10">
                      {/* Filter buttons */}
                      <div className="flex">
                        <div className="flex bg-zinc-900 rounded-lg border border-zinc-700 overflow-hidden">
                          {["approved", "declined", "waiting"].map((type) => (
                            <button
                              key={type}
                              className={`px-4 py-1 text-xs font-medium transition-colors ${
                                decisionFilter === type
                                  ? type === "approved"
                                    ? "bg-green-600 text-white"
                                    : type === "declined"
                                    ? "bg-red-600 text-white"
                                    : "bg-zinc-700 text-white"
                                  : "text-zinc-300 hover:bg-zinc-800"
                              }`}
                              onClick={() => {
                                setSearchParams((prev) => {
                                  const params = new URLSearchParams(prev);
                                  if (decisionFilter === type) {
                                    params.delete("decision");
                                  } else {
                                    params.set("decision", type);
                                  }
                                  return params;
                                });
                              }}
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                          ))}
                        </div>
                        <div className="ml-4 text-sm flex items-center dark:text-zinc-500">
                          {stepRunDecisions.count} decisions
                        </div>
                      </div>
                      <div>
                        <Search
                          onSearch={(search: string) => setSearch(search)}
                        />
                      </div>
                    </div>

                    <div>
                      <Table
                        headers={[
                          "Flow Run ID",
                          "Name",
                          "Decision",
                          "Reason",
                          "Reviewed By",
                          "Step Status",
                          "",
                        ]}
                        records={stepRunDecisions.results}
                        rowRenderer={(record) => (
                          <>
                            <td
                              className="px-6 py-2 text-sm font-medium text-gray-500 pl-8 cursor-pointer"
                              onClick={() => {
                                setSelectedFlowRunId(record.stepRun.flowRun.id);
                                setFlowRunModalOpen(true);
                              }}
                            >
                              <div className="flex items-center space-x-2">
                                <div className="flex flex-shrink-0 -space-x-1">
                                  <h2 className="min-w-0 text-sm/6 dark:text-zinc-400">
                                    <p className="flex gap-x-2">
                                      <span className="truncate">
                                        {record.stepRun.flowRun.id}
                                        <EyeIcon className="inline h-4 w-4 text-gray-400 ml-1" />
                                      </span>
                                    </p>
                                  </h2>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-2 text-sm font-medium text-gray-500 pl-6">
                              <div className="flex items-center space-x-2">
                                <div className="flex flex-shrink-0 -space-x-1">
                                  <h2 className="min-w-0 text-sm/6 dark:text-zinc-400">
                                    <p className="flex gap-x-2">
                                      <span className="truncate">
                                        {record.stepRun.flowRun.displayName}
                                      </span>
                                    </p>
                                  </h2>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-2 text-sm font-medium text-gray-500 pl-6">
                              <div className="flex items-center space-x-2">
                                <div className="flex flex-shrink-0 -space-x-1">
                                  <h2 className="min-w-0 text-sm/6 dark:text-zinc-400">
                                    <p className="flex gap-x-2">
                                      {record.decision === "declined" && (
                                        <span className="inline-flex items-center rounded-md bg-red-400/10 px-2 py-1 text-xs font-medium text-red-400 ring-1 ring-inset ring-red-400/20">
                                          {toSentenceCase(record.decision)}
                                        </span>
                                      )}
                                      {record.decision === "approved" && (
                                        <span className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-400 ring-1 ring-inset ring-green-400/20">
                                          {toSentenceCase(record.decision)}
                                        </span>
                                      )}
                                      {record.decision === "pending" && (
                                        <span className="inline-flex items-center rounded-md bg-yellow-400/10 px-2 py-1 text-xs font-medium text-yellow-400 ring-1 ring-inset ring-yellow-400/20">
                                          {toSentenceCase(record.decision)}
                                        </span>
                                      )}
                                    </p>
                                  </h2>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-2 text-sm font-medium text-gray-500 pl-6">
                              <div className="flex items-center space-x-2">
                                <div className="flex flex-shrink-0 -space-x-1">
                                  <h2 className="min-w-0 text-sm/6 dark:text-zinc-400">
                                    <p className="flex gap-x-2">
                                      <span className="truncate">
                                        {record.reason
                                          ? record.reason
                                          : "No reason given"}
                                      </span>
                                    </p>
                                  </h2>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-2 text-sm font-medium text-gray-500 pl-6">
                              <div className="flex items-center space-x-2">
                                <div className="flex flex-shrink-0 -space-x-1">
                                  <h2 className="min-w-0 text-sm/6 dark:text-zinc-400">
                                    <p className="flex gap-x-2">
                                      <span className="truncate">
                                        {record.madeBy}
                                      </span>
                                    </p>
                                  </h2>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-2 text-sm font-medium text-gray-500 pl-6">
                              <div className="flex items-center space-x-2">
                                <div className="flex flex-shrink-0 -space-x-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setIsOverrideModalOpen(true);
                                      setSetDecisionToOverride(
                                        record as StepRunDecision
                                      );
                                    }}
                                    className="min-w-0 text-sm/6 dark:text-zinc-400"
                                  >
                                    <p className="flex gap-x-2">
                                      <span className="truncate">Override</span>
                                    </p>
                                  </button>
                                </div>
                              </div>
                            </td>
                          </>
                        )}
                      />

                      <Pagination
                        currentPage={page}
                        totalPages={Math.ceil(stepRunDecisions.count / 15)}
                        onPageChange={(page: number) => setPage(page)}
                      />
                    </div>
                  </div>
                </>
                // <pre className="text-xs bg-zinc-900 text-white rounded p-4">
                //   {JSON.stringify(stepRunDecisions, null, 2)}
                // </pre>
              )}
            </div>
          ) : (
            <div className="mb-40">
              {flowStats?.decisionStats.map((stat) => {
                const approved = stat.decisions.approved || 0;
                const declined = stat.decisions.declined || 0;
                const pending = stat.decisions.pending || 0;
                const total = approved + declined + pending || 1;
                const approvedPct = (approved / total) * 100;
                const declinedPct = (declined / total) * 100;
                const pendingPct = (pending / total) * 100;
                return (
                  <div
                    className="dark:bg-zinc-800 w-1/2 mb-4 p-2 rounded-lg cursor-pointer hover:bg-zinc-700 transition-colors"
                    key={stat.stepId}
                    onClick={() => setSelectedStepId(stat.stepId.toString())}
                  >
                    <h4 className="mb-1 text-sm text-zinc-300">
                      {stat.stepName}
                    </h4>
                    <div className="flex items-center mb-1">
                      <span className="text-green-500 text-xs mr-2">
                        {approved} Approved
                      </span>
                      <span className="text-red-500 text-xs mr-2">
                        {declined} Declined
                      </span>
                      <span className="text-gray-400 text-xs">
                        {pending} Waiting
                      </span>
                    </div>
                    <div className="mt-1 bg-gray-200 dark:bg-zinc-800 w-full h-2 rounded overflow-hidden flex">
                      <div
                        className="bg-green-500 h-full"
                        style={{ width: `${approvedPct}%` }}
                      />
                      <div
                        className="bg-red-500 h-full"
                        style={{ width: `${declinedPct}%` }}
                      />
                      <div
                        className="bg-gray-400 dark:bg-zinc-600 h-full"
                        style={{ width: `${pendingPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Welcome to the overview. We want to show:
      <ul className="list-disc pl-4">
        <li>
          Approvals area that shows, for each "Approval Step", the number of
          approved steps, number of declined and number waiting. Clickable to
          see
        </li>
        <li>Pie chart showing the number of ones waiting, running etc.</li>
        <li>Show flows that are waiting/delay</li>
        <li>Show flows condition results</li>
      </ul> */}
    </div>
  );
};
